/**
 * Landing Page — PatrolPuffin Marketing Portal
 *
 * Homepage with hero, stats, features, how-it-works, testimonials, FAQ, and CTA.
 * Uses i18n for all user-facing text and analytics for page tracking.
 *
 * @module portals/marketing/pages/LandingPage
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { getBrand } from '@puffin-core/brand-assets';
import {
  MarketingSeo,
  CTASection,
  useAnalytics,
  organization,
  softwareApplication,
  faqPage,
  webPage,
  SmartLink,
  signupUrlFor,
} from '@puffin-core/marketing-shell';
import { PATROL_MARKETING_ROUTES } from '../constants/patrolMarketingRoutePaths';

/** "Get Started Free": the ONE signup URL with PatrolPuffin named (APP-ENTITLEMENT-001 Phase 11). */
const SIGNUP_HREF = signupUrlFor({ app: 'patrol' });

const APP_KEY = 'patrol';

/**
 * Stats displayed on the landing page
 * @constant {Array<Object>}
 */
const STATS = [
  { valueKey: 'landing.stats.auditsValue', labelKey: 'landing.stats.auditsLabel' },
  { valueKey: 'landing.stats.recoveredValue', labelKey: 'landing.stats.recoveredLabel' },
  { valueKey: 'landing.stats.accuracyValue', labelKey: 'landing.stats.accuracyLabel' },
  { valueKey: 'landing.stats.monitoringValue', labelKey: 'landing.stats.monitoringLabel' },
];

/**
 * Feature data for the features section
 * @constant {Array<Object>}
 */
const FEATURES = [
  {
    titleKey: 'Invoice Audit',
    descriptionKey:
      'Import carrier invoices from UPS, FedEx, and USPS. Our engine automatically matches every charge to the original quote.',
  },
  {
    titleKey: 'Rate Mismatch Detection',
    descriptionKey:
      'Compare quoted rates against billed amounts. Flag discrepancies above your threshold for review or auto-claim.',
  },
  {
    titleKey: 'Late Delivery Claims',
    descriptionKey:
      'Track service guarantee violations. Automatically identify eligible late deliveries and file refund claims.',
  },
  {
    titleKey: 'DIM Weight Analysis',
    descriptionKey:
      'Detect dimensional weight overcharges by comparing declared dimensions to carrier-billed DIM weight.',
  },
  {
    titleKey: 'Surcharge Audit',
    descriptionKey:
      'Catch address correction fees on valid addresses, residential surcharges on commercial addresses, and fuel surcharge errors.',
  },
  {
    titleKey: 'Multi-Carrier Support',
    descriptionKey:
      'USPS, UPS, and FedEx from day one. Automated USPS claims via API, plus formatted dispute reports for UPS and FedEx.',
  },
];

/**
 * How-it-works steps
 * @constant {Array<Object>}
 */
const STEPS = [
  {
    number: 1,
    title: 'Connect Your Carriers',
    description:
      'Upload UPS and FedEx invoice CSVs, or connect your USPS account for automated ingestion.',
  },
  {
    number: 2,
    title: 'We Audit Every Invoice',
    description:
      'Our reconciliation engine matches charges to quotes and runs 10 detection rules to find billing errors.',
  },
  {
    number: 3,
    title: 'Recover Credits',
    description:
      'File claims automatically via USPS API or generate formatted dispute reports for UPS and FedEx portals.',
  },
];

/**
 * Hero section with headline, subtitle, and CTA buttons
 *
 * @param {Object} props - Component props
 * @param {Function} props.t - Translation function
 * @returns {React.JSX.Element} Hero section
 */
const HeroSection = ({ t }) => (
  <section className="hero">
    <div className="hero-content">
      <h1 className="hero-title">{t('landing.hero.title')}</h1>
      <p className="hero-subtitle">{t('landing.hero.subtitle')}</p>
      <div className="hero-actions">
        <SmartLink href={SIGNUP_HREF} className="btn btn-primary btn-lg">
          {t('landing.hero.ctaPrimary')}
        </SmartLink>
        <Link to={PATROL_MARKETING_ROUTES.FEATURES} className="btn btn-secondary btn-lg">
          {t('landing.hero.ctaSecondary')}
        </Link>
      </div>
    </div>
  </section>
);

HeroSection.propTypes = {
  t: PropTypes.func.isRequired,
};

/**
 * Stats section showing key metrics
 *
 * @param {Object} props - Component props
 * @param {Function} props.t - Translation function
 * @returns {React.JSX.Element} Stats section
 */
const StatsSection = ({ t }) => (
  <section className="section-marketing section-light" id="stats">
    <div className="container">
      <div className="stats-grid">
        {STATS.map((stat) => (
          <div key={stat.valueKey}>
            <span className="stat-value">{t(stat.valueKey)}</span>
            <span className="stat-label">{t(stat.labelKey)}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

StatsSection.propTypes = {
  t: PropTypes.func.isRequired,
};

/**
 * Feature card sub-component
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Feature title
 * @param {string} props.description - Feature description
 * @returns {React.JSX.Element} Feature card
 */
const FeatureCard = ({ title, description }) => (
  <div className="feature-card">
    <h3 className="feature-card-title">{title}</h3>
    <p className="feature-card-description">{description}</p>
  </div>
);

FeatureCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

/**
 * Features section with 6 feature cards
 *
 * @param {Object} props - Component props
 * @param {Function} props.t - Translation function
 * @returns {React.JSX.Element} Features section
 */
const FeaturesSection = ({ t }) => (
  <section className="section-marketing" id="features">
    <div className="container">
      <h2 className="section-heading">{t('landing.features.title')}</h2>
      <p className="section-description">{t('landing.features.subtitle')}</p>
      <div className="feature-grid">
        {FEATURES.map((feature) => (
          <FeatureCard
            key={feature.titleKey}
            title={feature.titleKey}
            description={feature.descriptionKey}
          />
        ))}
      </div>
    </div>
  </section>
);

FeaturesSection.propTypes = {
  t: PropTypes.func.isRequired,
};

/**
 * Step card for how-it-works section
 *
 * @param {Object} props - Component props
 * @param {number} props.number - Step number
 * @param {string} props.title - Step title
 * @param {string} props.description - Step description
 * @returns {React.JSX.Element} Step card
 */
const StepCard = ({ number, title, description }) => (
  <div className="step-card">
    <span className="step-number">{number}</span>
    <h3 className="step-card-title">{title}</h3>
    <p className="step-card-description">{description}</p>
  </div>
);

StepCard.propTypes = {
  number: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

/**
 * How it works section with 3 steps
 *
 * @param {Object} props - Component props
 * @param {Function} props.t - Translation function
 * @returns {React.JSX.Element} How it works section
 */
const HowItWorksSection = ({ t }) => (
  <section className="section-marketing" id="how-it-works">
    <div className="container">
      <h2 className="section-heading">{t('landing.howItWorks.title')}</h2>
      <p className="section-description">{t('landing.howItWorks.subtitle')}</p>
      <div className="steps-grid">
        {STEPS.map((step) => (
          <StepCard
            key={step.number}
            number={step.number}
            title={step.title}
            description={step.description}
          />
        ))}
      </div>
    </div>
  </section>
);

HowItWorksSection.propTypes = {
  t: PropTypes.func.isRequired,
};

/**
 * Testimonial card sub-component
 *
 * @param {Object} props - Component props
 * @param {string} props.quote - Testimonial quote text
 * @param {string} props.author - Author name
 * @param {string} props.role - Author role
 * @param {string} props.company - Author company
 * @returns {React.JSX.Element} Testimonial card
 */
const TestimonialCard = ({ quote, author, role, company }) => (
  <blockquote className="testimonial-card">
    <p className="testimonial-quote">&ldquo;{quote}&rdquo;</p>
    <footer className="testimonial-attribution">
      <strong className="testimonial-name">{author}</strong>
      <span className="testimonial-role">
        {role}, {company}
      </span>
    </footer>
  </blockquote>
);

TestimonialCard.propTypes = {
  quote: PropTypes.string.isRequired,
  author: PropTypes.string.isRequired,
  role: PropTypes.string.isRequired,
  company: PropTypes.string.isRequired,
};

/**
 * Testimonials section with customer quotes
 *
 * @param {Object} props - Component props
 * @param {Function} props.t - Translation function
 * @returns {React.JSX.Element} Testimonials section
 */
const TestimonialsSection = ({ t }) => {
  const items = t('landing.testimonials.items', { returnObjects: true });
  const testimonials = Array.isArray(items) ? items : [];

  return (
    <section className="section-marketing" id="testimonials" aria-labelledby="testimonials-title">
      <div className="container">
        <h2 id="testimonials-title" className="section-heading">
          {t('landing.testimonials.title')}
        </h2>
        <div className="testimonial-grid">
          {testimonials.map((item) => (
            <TestimonialCard
              key={item.author}
              quote={item.quote}
              author={item.author}
              role={item.role}
              company={item.company}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

TestimonialsSection.propTypes = {
  t: PropTypes.func.isRequired,
};

/**
 * FAQ item with accordion behavior
 *
 * @param {Object} props - Component props
 * @param {string} props.question - FAQ question
 * @param {string} props.answer - FAQ answer
 * @param {boolean} props.isOpen - Whether this item is expanded
 * @param {Function} props.onToggle - Toggle handler
 * @returns {React.JSX.Element} FAQ accordion item
 */
const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="faq-item">
    <button
      type="button"
      className={`faq-question${isOpen ? ' faq-question-active' : ''}`}
      onClick={onToggle}
      aria-expanded={isOpen}
    >
      <span>{question}</span>
      <span className="faq-toggle" aria-hidden="true">
        {isOpen ? '\u2212' : '+'}
      </span>
    </button>
    {isOpen && (
      <div className="faq-answer" role="region">
        <p>{answer}</p>
      </div>
    )}
  </div>
);

FAQItem.propTypes = {
  question: PropTypes.string.isRequired,
  answer: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

/**
 * FAQ section with accordion items
 *
 * @param {Object} props - Component props
 * @param {Function} props.t - Translation function
 * @returns {React.JSX.Element} FAQ section
 */
const FAQSection = ({ t }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const items = t('landing.faq.items', { returnObjects: true });
  const faqItems = Array.isArray(items) ? items : [];

  const handleToggle = useCallback((index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  }, []);

  return (
    <section className="section-marketing" id="faq" aria-labelledby="faq-title">
      <div className="container">
        <h2 id="faq-title" className="section-heading">
          {t('landing.faq.title')}
        </h2>
        <div className="faq-list">
          {faqItems.map((item, index) => (
            <FAQItem
              key={item.question}
              question={item.question}
              answer={item.answer}
              isOpen={openIndex === index}
              onToggle={() => handleToggle(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

FAQSection.propTypes = {
  t: PropTypes.func.isRequired,
};

const LANDING_TITLE = 'PatrolPuffin - Carrier Invoice Audit & Shipping Cost Recovery';
const LANDING_DESCRIPTION =
  'Audit UPS, FedEx, and USPS invoices automatically. Detect billing errors and recover shipping overcharges.';
const LANDING_CANONICAL = 'https://patrolpuffin.com/';

/**
 * Landing Page Component
 *
 * PatrolPuffin marketing homepage with hero, stats, features,
 * how-it-works, testimonials, FAQ, and CTA sections.
 *
 * @returns {React.JSX.Element} Landing page
 */
const LandingPage = () => {
  const { t } = useTranslation();
  const { trackPageView } = useAnalytics();
  const brand = getBrand(APP_KEY);

  useEffect(() => {
    trackPageView('/', 'PatrolPuffin Home');
  }, [trackPageView]);

  const jsonLd = useMemo(() => {
    const faqItems = t('landing.faq.items', { returnObjects: true });
    const faqs = Array.isArray(faqItems) ? faqItems : [];
    const schemas = [
      organization({
        appKey: APP_KEY,
        brand,
        description: 'Automated carrier invoice auditing for postal retailers.',
      }),
      softwareApplication({
        appKey: APP_KEY,
        brand,
      }),
      webPage({
        appKey: APP_KEY,
        brand,
        url: LANDING_CANONICAL,
        title: LANDING_TITLE,
        description: LANDING_DESCRIPTION,
      }),
    ];
    // `faqPage()` throws on empty arrays — only include the FAQPage
    // schema when at least one FAQ item resolves (avoids brittle render
    // failure in tests that stub i18n.t with non-array fallbacks).
    if (faqs.length > 0) {
      schemas.push(
        faqPage({ items: faqs.map((f) => ({ question: f.question, answer: f.answer })), t }),
      );
    }
    return schemas;
  }, [t, brand]);

  const ogImageUrl = `https://patrolpuffin.com${brand.assets.ogCard}`;

  return (
    <>
      <MarketingSeo
        title={LANDING_TITLE}
        description={LANDING_DESCRIPTION}
        canonical={LANDING_CANONICAL}
        ogType="website"
        ogImage={ogImageUrl}
        jsonLd={jsonLd}
      />

      <HeroSection t={t} />
      <StatsSection t={t} />
      <FeaturesSection t={t} />
      <HowItWorksSection t={t} />
      <TestimonialsSection t={t} />
      <FAQSection t={t} />
      <CTASection
        heading={t('landing.cta.title')}
        subheading={t('landing.cta.subtitle')}
        primaryAction={t('landing.cta.button')}
        primaryLink={PATROL_MARKETING_ROUTES.CONTACT}
      />
    </>
  );
};

export default LandingPage;
